import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../App';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { Logo } from '../Logo';
import {
  LayoutDashboard, Users, CreditCard, Settings,
  QrCode, FileBarChart, Menu, LogOut, ChevronRight,
  UserCheck, Calendar, UsersRound, Mail, MessageSquare,
  Wallet, KeyRound, BarChart3, Gift, Ticket, Coins, BadgeIndianRupee,
  LineChart, DatabaseBackup, Server, Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Sidebar schema
 * ─────────────────────────────────────────────────────────────────────────
 * Each entry is either:
 *   • a leaf  : { path, label, icon, allowedRoles?, superAdminOnly? }
 *   • a group : { groupId, label, icon, allowedRoles?, children: [leaf,…] }
 *
 * Groups collapse into an expandable dropdown — useful when many related
 * pages would otherwise crowd the sidebar (e.g. Points · *). A group is
 * auto-expanded when the current route lives inside it.
 *
 * Role matrix (per-fixed-role) — keep this comment in sync with backend.
 *   super_admin : everything
 *   manager     : everything except Users (super-admin only)
 *   reception   : Dashboard, Operations, Check-ins, Points (Scan/Manual),
 *                 Scan Pass, Bookings, Customers
 *   marketing   : Dashboard, Events, Marketing, WhatsApp Campaigns, Customers
 *                 (Customers needed for segmentation)
 *   gate_scan   : ONLY Scan Pass — locked down to a single page
 *   finance_viewer: Dashboard, Payments, Reports (read-only)
 */
const ROLES_OPS         = ['super_admin', 'manager', 'reception'];
const ROLES_MARKETING   = ['super_admin', 'manager', 'marketing'];               // Events page — actual marketing team
const ROLES_MKT_CAMPAIGN = ['super_admin', 'manager', 'marketing', 'special_ops']; // Marketing/WhatsApp — includes SXE ops
const ROLES_GATE_SCAN   = ['super_admin', 'manager', 'reception', 'gate_scan'];
const ROLES_FINANCE     = ['super_admin', 'manager', 'finance_viewer'];
const ROLES_ADMIN_OR_MGR = ['super_admin', 'manager'];
const ROLES_ANALYTICS    = ['super_admin', 'manager', 'marketing'];
const ROLES_SXE          = ['super_admin', 'manager', 'special_ops'];
const ROLES_SXE_OPS      = ['super_admin', 'manager', 'reception', 'special_ops'];
const ROLES_SXE_SCAN     = ['super_admin', 'manager', 'reception', 'gate_scan', 'special_ops'];

const NAV_ITEMS = [
  { path: '/admin/dashboard',  label: 'Dashboard',       icon: LayoutDashboard, allowedRoles: ['super_admin', 'manager', 'reception', 'marketing', 'finance_viewer', 'special_ops'] },
  { path: '/admin/operations', label: 'Operations',      icon: UserCheck,       allowedRoles: ROLES_OPS },
  { path: '/admin/checkins',   label: 'Check-ins',       icon: UserCheck,       allowedRoles: ROLES_OPS },
  {
    groupId: 'points',
    label:   'Points',
    icon:    Coins,
    children: [
      { path: '/admin/points/scan',          label: 'Scan QR',     icon: Wallet,    allowedRoles: ROLES_OPS },
      { path: '/admin/points/manual-redeem', label: 'Manual OTP',  icon: KeyRound,  allowedRoles: ROLES_OPS },
      { path: '/admin/points/issue',         label: 'Issue',       icon: Gift,      allowedRoles: ['super_admin'] },
      { path: '/admin/points/analytics',     label: 'Analytics',   icon: BarChart3, allowedRoles: ROLES_ADMIN_OR_MGR },
    ],
  },
  { path: '/admin/payments',            label: 'Payments',           icon: CreditCard,    allowedRoles: ROLES_FINANCE },
  { path: '/admin/gate-codes',          label: 'Gate Codes',         icon: CreditCard,    allowedRoles: ['super_admin', 'manager'] },
  {
    groupId: 'special-events',
    label:   'Special Events',
    icon:    Sparkles,
    children: [
      { path: '/admin/special-events',          label: 'Inventory & Codes', icon: Sparkles, end: true, allowedRoles: ROLES_SXE },
      { path: '/admin/special-events/bookings', label: 'Bookings',          icon: Ticket,   allowedRoles: ROLES_SXE_OPS },
      { path: '/admin/special-events/scan',     label: 'Scan Pass',         icon: QrCode,   allowedRoles: ROLES_SXE_SCAN },
    ],
  },
  { path: '/admin/pricing-rules',       label: 'Pricing Rules',      icon: Settings,      allowedRoles: ROLES_ADMIN_OR_MGR },
  { path: '/admin/events',              label: 'Events',             icon: Calendar,      allowedRoles: ROLES_MARKETING },
  { path: '/admin/scan-pass',           label: 'Scan Pass',          icon: QrCode,        allowedRoles: ROLES_GATE_SCAN },
  { path: '/admin/bookings',            label: 'Bookings',           icon: Ticket,        allowedRoles: ROLES_OPS },
  { path: '/admin/customers',           label: 'Customers',          icon: UsersRound,    allowedRoles: ['super_admin', 'manager', 'reception', 'marketing', 'special_ops'] },
  { path: '/admin/marketing',           label: 'Marketing',          icon: Mail,          allowedRoles: ROLES_MKT_CAMPAIGN },
  { path: '/admin/marketing/whatsapp',  label: 'WhatsApp Campaigns', icon: MessageSquare, allowedRoles: ROLES_MKT_CAMPAIGN },
  { path: '/admin/promoters',           label: 'Promoters',          icon: BadgeIndianRupee, allowedRoles: ROLES_ADMIN_OR_MGR },
  { path: '/admin/analytics',           label: 'Analytics',          icon: LineChart,        allowedRoles: ROLES_ANALYTICS },
  { path: '/admin/reports',             label: 'Reports',            icon: FileBarChart,  allowedRoles: ROLES_FINANCE },
  { path: '/admin/users',               label: 'Users',              icon: Users,         superAdminOnly: true },
  { path: '/admin/backups',             label: 'Backups',            icon: DatabaseBackup, superAdminOnly: true },
  { path: '/admin/server',              label: 'Server Usage',       icon: Server,         allowedRoles: ROLES_ADMIN_OR_MGR },
];

// ── Role guards ─────────────────────────────────────────────────────────────
function isLeafVisible(item, user) {
  if (item.superAdminOnly && user?.role !== 'super_admin') return false;
  if (item.allowedRoles && !item.allowedRoles.includes(user?.role)) return false;
  return true;
}

function visibleChildren(group, user) {
  return (group.children || []).filter((c) => isLeafVisible(c, user));
}

// ── Leaf nav link ───────────────────────────────────────────────────────────
function NavLeaf({ item, user, onClick, indent = false }) {
  if (!isLeafVisible(item, user)) return null;
  return (
    <NavLink
      to={item.path}
      end={item.end === true}
      onClick={onClick}
      data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-')}`}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 py-2.5 rounded-r-lg mx-2 text-sm font-medium transition-colors duration-150',
          indent ? 'pl-10 pr-4' : 'px-4',
          isActive
            ? 'sidebar-active'
            : 'text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A]'
        )
      }
    >
      <item.icon size={indent ? 14 : 16} />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

// ── Collapsible group ───────────────────────────────────────────────────────
function NavGroup({ group, user, onNavClick, isOpen, onToggle }) {
  const { pathname } = useLocation();
  const children = visibleChildren(group, user);
  if (children.length === 0) return null; // hide whole group if no access

  const containsActive = children.some((c) => pathname.startsWith(c.path));

  return (
    <div className="select-none">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`nav-group-${group.groupId}`}
        data-testid={`nav-group-${group.groupId}-toggle`}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-2.5 rounded-r-lg mx-2 text-sm font-medium transition-colors duration-150',
          containsActive
            ? 'text-[#F2F2F2] bg-[#1A1A1A]'
            : 'text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A]',
        )}
        style={{ width: 'calc(100% - 1rem)' }}
      >
        <group.icon size={16} />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronRight
          size={14}
          className={cn(
            'text-[#7A7A7A] transition-transform duration-200',
            isOpen && 'rotate-90',
          )}
        />
      </button>

      {/* Children — height-animated collapse */}
      <div
        id={`nav-group-${group.groupId}`}
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-200 ease-out',
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        )}
        data-testid={`nav-group-${group.groupId}-panel`}
      >
        <div className="py-0.5 space-y-0.5">
          {children.map((c) => (
            <NavLeaf key={c.path} item={c} user={user} onClick={onNavClick} indent />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar content ─────────────────────────────────────────────────────────
function SidebarContent({ user, onLogout, onNavClick }) {
  const { pathname } = useLocation();

  // Auto-open any group whose route is currently active. Keep manually
  // toggled state in component so the user can collapse/expand at will.
  const initialOpen = useMemo(() => {
    const map = {};
    NAV_ITEMS.forEach((it) => {
      if (it.groupId) {
        map[it.groupId] = (it.children || []).some((c) => pathname.startsWith(c.path));
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [openMap, setOpenMap] = useState(initialOpen);

  // If user navigates to a child route from elsewhere, ensure the group opens
  useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      let changed = false;
      NAV_ITEMS.forEach((it) => {
        if (it.groupId && (it.children || []).some((c) => pathname.startsWith(c.path))) {
          if (!next[it.groupId]) {
            next[it.groupId] = true;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleGroup = (groupId) => {
    setOpenMap((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <Logo variant="icon" height={32} />
          <div>
            <p className="text-[10px] text-[#B9B9B9] uppercase tracking-wider">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          if (item.groupId) {
            return (
              <NavGroup
                key={item.groupId}
                group={item}
                user={user}
                onNavClick={onNavClick}
                isOpen={!!openMap[item.groupId]}
                onToggle={() => toggleGroup(item.groupId)}
              />
            );
          }
          return <NavLeaf key={item.path} item={item} user={user} onClick={onNavClick} />;
        })}
      </nav>

      {/* User + Logout */}
      <div className="border-t border-[#2A2A2A] p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#D4A843]/20 border border-[#D4A843]/40 flex items-center justify-center">
            <span className="text-[#D4A843] text-xs font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#F2F2F2] font-medium truncate">{user?.name}</p>
            <p className="text-xs text-[#B9B9B9] capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-[#B9B9B9] hover:text-[#F2F2F2] hover:bg-[#1A1A1A] gap-2"
          onClick={onLogout}
          data-testid="admin-logout-button"
        >
          <LogOut size={14} />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AdminShell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-56 border-r border-[#2A2A2A] fixed inset-y-0 left-0 z-30" style={{ backgroundColor: '#111111' }}>
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-56 border-r border-[#2A2A2A]" style={{ backgroundColor: '#111111' }}>
          <SidebarContent user={user} onLogout={handleLogout} onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-20 border-b border-[#2A2A2A] px-4 lg:px-6 py-3 flex items-center gap-3" style={{ backgroundColor: '#111111' }}>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-[#B9B9B9]"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={18} />
          </Button>
          <div className="flex-1">
            {title && (
              <h1 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] font-medium">{title}</h1>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
